import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, FormField, Heading, Input } from '@insolech/ui';
import { supabase } from '@/lib/supabase';

/**
 * AuthResetPage — set a new password.
 *
 * Reached one of two ways:
 *   1. /auth/reset — manually after clicking the recovery link in their email
 *      (Supabase puts the user into an authenticated "recovery" session that
 *      only allows password update).
 *   2. /auth (Forgot password? link) → request a recovery email — this is the
 *      send-only request form below, toggled by `mode`.
 */
export function AuthResetPage() {
  const navigate = useNavigate();
  const [mode, setMode] = React.useState<'request' | 'set'>('request');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  // If we already have a session, jump straight into "set" mode (recovery flow).
  React.useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      if (data.session) setMode('set');
    });
  }, []);

  const onRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?type=recovery`,
    });
    setLoading(false);
    if (err) setError(err.message);
    else setInfo(`If an account exists for ${email}, a password reset link has been sent.`);
  };

  const onSet = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (err) setError(err.message);
    else navigate('/dashboard', { replace: true });
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-surface-base p-6">
      <div className="w-full max-w-md">
        <Heading as="h1" size="h3">
          {mode === 'set' ? 'Set a new password' : 'Reset your password'}
        </Heading>
        <p className="mt-2 text-text-secondary text-sm">
          {mode === 'set'
            ? 'Pick a strong password (at least 8 characters).'
            : 'Enter the email you sign in with and we will send you a reset link.'}
        </p>

        {info ? (
          <Alert tone="success" title="Check your inbox" className="mt-6">
            {info}
          </Alert>
        ) : null}
        {error ? (
          <Alert tone="error" title="Something went wrong" className="mt-6">
            {error}
          </Alert>
        ) : null}

        {mode === 'set' ? (
          <form className="mt-8 flex flex-col gap-4" onSubmit={onSet}>
            <FormField label="New password" required>
              <Input
                type="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </FormField>
            <FormField label="Confirm new password" required>
              <Input
                type="password"
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </FormField>
            <Button type="submit" size="lg" disabled={loading}>
              {loading ? 'Saving…' : 'Save new password'}
            </Button>
          </form>
        ) : (
          <form className="mt-8 flex flex-col gap-4" onSubmit={onRequest}>
            <FormField label="Email" required>
              <Input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormField>
            <Button type="submit" size="lg" disabled={loading}>
              {loading ? 'Sending…' : 'Send reset link'}
            </Button>
            <a
              href="/auth"
              className="text-sm text-brand-600 hover:text-brand-700 self-center mt-2 max-sm:py-2 max-sm:-mb-2"
            >
              Back to sign in
            </a>
          </form>
        )}
      </div>
    </main>
  );
}
